// app/src/features/submit/useSubmitEffects.js
import { useEffect, useRef } from "react";
import { useAskHeroSubmit } from "../../hooks/useAskHeroSubmit.js";
import { useFeaturedForecasts } from "../../hooks/useFeaturedForecasts.js";
import { useResearchQuestions } from "../../hooks/useResearchQuestions.js";
import { captureChatTarget } from "../../lib/forecast/chatTarget.js";
import { listedThreads, researchOf, streamsResearch } from "../../lib/forecast/deepProgress.js";
import { closesOnCompletion } from "../../lib/forecast/preprocessContinue.js";
import { showsRunStatus } from "../../lib/forecast/progressSteps.js";
import { shouldShowIntroduction } from "../../lib/forecast/restore.js";
import { selectActiveRun, selectRun } from "../../lib/forecast/selectors.js";
import { POST_TOUR_GENERATING_NOTE } from "../../onboarding/sayerTour";

// A run status panel with no research run on it lists no research questions.
const NO_THREADS = Object.freeze([]);

/**
 * The submit bucket's late hooks and the slots' render-time values: the featured forecasts of the
 * new-question page, the ask-hero submit, the preprocess panel closing once its forecast completes,
 * the post-tour "generating" note (once the preprocess panel closes), the Enter-anywhere send, the run status card's research questions, and the hero / status
 * card gates (`askHeroOn`, `heroPendingRun`, `statusRun`, `runStatusOn`). Called where `AppInner`
 * called `useAskHeroSubmit`: after the chat intent (`handleChatMessage`) and the tour controller
 * (`tourActive`), before the auth early returns — a hook cannot sit after them, a const can be
 * computed before them from the same inputs.
 *
 * @param {object} deps
 * @param {Function} deps.getRunState  the run store's live reader (injected, never imported — C-23)
 * @param {object} deps.runState  the run store's render-time state
 */
export function useSubmitEffects({
  active,
  runState,
  getRunState,
  handleSelectRef,
  setTrackingSettingsId,
  moveForecastToTop,
  handleChatMessage,
  forecastEngine,
  pendingPreprocess,
  setPendingPreprocess,
  ninetyNotePendingRef,
  pushSayerMsg,
  input,
  selEvt,
  bugOpen,
  showReport,
  customEvtPopover,
  isPhone,
  compareOn,
  forecastLiveProgressEnabled,
  bootPhase,
  pendingRestore,
  tourActive,
}) {
  // "Forecasts you may be interested in" on the new-question page.
  const featuredForecasts = useFeaturedForecasts({
    handleSelectRef,
    onOpened: (runId) => {
      setTrackingSettingsId(null);
      if (runId) moveForecastToTop(runId);
    },
  });

  // A question asked on the new-question screen: its progress card at once, and Sayer's reason
  // on that screen when it does not become a forecast.
  const askHeroSubmit = useAskHeroSubmit({
    handleChatMessage,
    getState: getRunState,
    activeId: active?.id ?? null,
    engine: forecastEngine,
  });

  // The panel never blocks the forecast, so the forecast can finish before the user confirms: it
  // then closes itself. The tour's "usually takes a minute or two" note is moot by then and is
  // dropped with it. Only the panel's own run closes it; a newer panel is left standing.
  const ppRunStatus = pendingPreprocess
    ? (selectRun(runState, pendingPreprocess.clientId)?.status ?? null)
    : null;
  useEffect(() => {
    if (!closesOnCompletion(pendingPreprocess, ppRunStatus)) return;
    const { clientId } = pendingPreprocess;
    ninetyNotePendingRef.current = false;
    setPendingPreprocess((p) => (p?.clientId === clientId ? null : p));
  }, [pendingPreprocess, ppRunStatus, setPendingPreprocess, ninetyNotePendingRef]);

  // Posts the note only once the preprocess/contract panel is DISMISSED (Continue or timeout),
  // after a delay, on the non-null→null edge of pendingPreprocess.
  const prevPreprocessOpenRef = useRef(false);
  useEffect(() => {
    const open = !!pendingPreprocess;
    if (prevPreprocessOpenRef.current && !open && ninetyNotePendingRef.current) {
      ninetyNotePendingRef.current = false;
      // Captured now: the note is about the forecast whose panel just closed, and the user can
      // select another one before the delay ends.
      const target = captureChatTarget(getRunState());
      const t = setTimeout(
        () => pushSayerMsg(POST_TOUR_GENERATING_NOTE, { kind: "tour", target }),
        2000,
      );
      prevPreprocessOpenRef.current = open;
      return () => clearTimeout(t);
    }
    prevPreprocessOpenRef.current = open;
  }, [pendingPreprocess, pushSayerMsg]); // eslint-disable-line react-hooks/exhaustive-deps -- ninetyNotePendingRef is AppInner's tour useRef (stable) and getRunState the store's module-level reader (an import at BASE), both hook inputs here; the array is BASE's

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (!input || !input.trim()) return;
      if (selEvt || bugOpen || pendingPreprocess || showReport || customEvtPopover) return;
      const t = e.target;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "BUTTON" ||
          t.isContentEditable)
      )
        return;
      e.preventDefault();
      handleChatMessage();
    };
    // telemetry-gesture: Enter anywhere sends the composer; handleChatMessage opens chat.ask
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [input, handleChatMessage, selEvt, bugOpen, pendingPreprocess, showReport, customEvtPopover]);

  // A research run's questions for its run status card. Read off the active forecast's run, before
  // the returns below: a research run on the card is always that run, never the ask-hero stand-in.
  const deepStatusRun = active?.id ? selectRun(runState, active.id) : null;
  const researchQuestions = useResearchQuestions({
    threads: deepStatusRun ? listedThreads(researchOf(deepStatusRun)) : NO_THREADS,
    question: deepStatusRun?.contract?.question || deepStatusRun?.question || "",
    // The phone layout has no run status card, so it never shows these questions.
    enabled:
      !isPhone &&
      !compareOn &&
      forecastLiveProgressEnabled &&
      streamsResearch(deepStatusRun) &&
      showsRunStatus(deepStatusRun, { liveProgress: true }),
  });

  // The canvas's empty-state hero and the running-forecast status card.
  const askHeroOn =
    !active?.id &&
    !compareOn &&
    shouldShowIntroduction({ bootPhase, hasActive: false, pendingRestore });
  // The run on screen, for the run-status panel (a render-time read). A question just asked on
  // the new-question screen shows its card before its run exists.
  const heroPendingRun = askHeroOn && !tourActive ? askHeroSubmit.pendingRun : null;
  const statusRun = heroPendingRun || selectActiveRun(runState);
  const runStatusOn =
    !compareOn &&
    !tourActive &&
    showsRunStatus(statusRun, { liveProgress: forecastLiveProgressEnabled });

  return {
    featuredForecasts,
    askHeroSubmit,
    researchQuestions,
    askHeroOn,
    heroPendingRun,
    statusRun,
    runStatusOn,
  };
}
